import React from "react";
import { Globe, Smartphone, Tablet, HelpCircle } from "lucide-react";
import type { UsageClient } from "../../api/admin";

// Shared bits for the admin usage surfaces (spec 14 / ATL-70).

export const CLIENT_LABEL: Record<UsageClient, string> = {
  web: "Web",
  ios: "iOS",
  android: "Android",
};

export const CLIENT_COLOR: Record<UsageClient, string> = {
  web: "var(--sky-500)",
  ios: "var(--accent)",
  android: "var(--spruce-500)",
};

export function ClientGlyph({
  client,
  size = 13,
  withLabel = false,
}: {
  client: UsageClient | string | null | undefined;
  size?: number;
  withLabel?: boolean;
}) {
  const style: React.CSSProperties = { verticalAlign: "-2px", flexShrink: 0 };
  let icon: React.ReactNode;
  let label: string;
  switch (client) {
    case "web":
      icon = <Globe size={size} style={style} />;
      label = CLIENT_LABEL.web;
      break;
    case "ios":
      icon = <Smartphone size={size} style={style} />;
      label = CLIENT_LABEL.ios;
      break;
    case "android":
      icon = <Tablet size={size} style={style} />;
      label = CLIENT_LABEL.android;
      break;
    default:
      icon = <HelpCircle size={size} style={style} />;
      label = "Unknown client";
  }
  return (
    <span
      title={label}
      aria-label={label}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--text-muted)" }}
    >
      {icon}
      {withLabel && <span style={{ fontSize: 12 }}>{label}</span>}
    </span>
  );
}

/** "peak_detail" -> "Peak detail"; raw fallbacks ("app:/x/y") pass through. */
export function screenLabel(name: string): string {
  if (name.includes(":")) return name;
  const words = name.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return "Never";
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return "—";
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days} d ago`;
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Segmented control used for days / client / metric pickers. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div
      role="group"
      style={{
        display: "inline-flex",
        background: "var(--surface-sunken)",
        borderRadius: "var(--radius-pill)",
        padding: 2,
        gap: 2,
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            style={{
              height: 26,
              padding: "0 10px",
              borderRadius: "var(--radius-pill)",
              border: "none",
              background: active ? "var(--surface-card)" : "transparent",
              color: active ? "var(--text-primary)" : "var(--text-muted)",
              fontSize: 12,
              fontWeight: active ? 600 : 500,
              cursor: "pointer",
              boxShadow: active ? "var(--shadow-sm)" : "none",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
